import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import characterService from '../../services/characterService';
import CharacterNetworkDiagram from '../../components/CharacterNetworkDiagram';
import IconicQuotesSection from './components/IconicQuotesSection';
import useTheme from '../../hooks/useTheme';
import useReadingProgress from '../../hooks/useReadingProgress';
import useScrollPersistence from '../../hooks/useScrollPersistence';
import ReadingProgressBar from '../play-reading-interface/components/ReadingProgressBar';

const CharacterDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [character, setCharacter] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [isLoadingNetwork, setIsLoadingNetwork] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  const [iconicQuotes, setIconicQuotes] = useState(null);
  const [isGeneratingQuotes, setIsGeneratingQuotes] = useState(false);
  const [quotesError, setQuotesError] = useState(null);

  const playId = location?.state?.playId;
  const characterName = location?.state?.characterName;
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  // Reading progress bar
  const readingProgress = useReadingProgress();

  // Scroll persistence per character (use characterName as "scene" key)
  const pageReady = !isLoading && !!character;
  const scrollKey = characterName ? characterName?.replace(/\s+/g, '-')?.toLowerCase() : null;
  useScrollPersistence(playId, 'char', scrollKey, pageReady);

  useEffect(() => {
    if (playId && characterName) {
      fetchCharacterDetails();
      fetchCharacterNetwork();
    }
  }, [playId, characterName]);

  const fetchCharacterDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await characterService?.getCharacterDetails(playId, characterName);
      setCharacter(data);

      // Load saved AI content from database
      if (data?.aiAnalysis) {
        setAiAnalysis(data?.aiAnalysis);
      }
      if (data?.aiQuotes) {
        // Fetch audio for saved quotes
        const lineIds = data?.aiQuotes?.map(q => q?.lineId)?.filter(Boolean);
        const audioMap = await characterService?.getAudioForLines(lineIds);
        
        // Merge audio data with quotes
        const quotesWithAudio = data?.aiQuotes?.map(quote => {
          const audioData = audioMap?.get(quote?.lineId);
          return {
            ...quote,
            audioUrl: audioData?.audioUrl || null,
            duration: audioData?.duration || null
          };
        });
        setIconicQuotes(quotesWithAudio);
      }

      // Update popularity score (view count)
      await characterService?.updatePopularityScore(playId, characterName, 1);
    } catch (err) {
      console.error('Error fetching character details:', err);
      setError(err?.message || 'Failed to load character details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCharacterNetwork = async () => {
    try {
      setIsLoadingNetwork(true);
      setNetworkError(null);
      const data = await characterService?.getCharacterRelationships(playId, characterName);
      setNetworkData(data);
    } catch (err) {
      console.error('Error fetching character network:', err);
      setNetworkError(err?.message || 'Failed to load character relationships');
    } finally {
      setIsLoadingNetwork(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    if (!character?.lines || character?.lines?.length === 0) {
      setError('No character lines available for analysis');
      return;
    }

    try {
      setIsGeneratingAnalysis(true);
      setError(null);
      const analysis = await characterService?.generateCharacterAnalysis(
        characterName,
        character?.playTitle,
        character?.lines,
        playId
      );
      setAiAnalysis(analysis);
    } catch (err) {
      console.error('Error generating analysis:', err);
      setError(err?.message || 'Failed to generate character analysis');
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  const handleGenerateQuotes = async () => {
    if (!character?.lines || character?.lines?.length === 0) {
      setQuotesError('No character lines available for quote generation');
      return;
    }

    try {
      setIsGeneratingQuotes(true);
      setQuotesError(null);
      
      // Generate quotes
      const quotes = await characterService?.generateIconicQuotes(
        characterName,
        character?.playTitle,
        character?.lines,
        playId
      );

      // Fetch audio for the quotes
      const lineIds = quotes?.map(q => q?.lineId)?.filter(Boolean);
      const audioMap = await characterService?.getAudioForLines(lineIds);

      // Merge audio data with quotes
      const quotesWithAudio = quotes?.map(quote => {
        const audioData = audioMap?.get(quote?.lineId);
        return {
          ...quote,
          audioUrl: audioData?.audioUrl || null,
          duration: audioData?.duration || null
        };
      });

      setIconicQuotes(quotesWithAudio);
    } catch (err) {
      console.error('Error generating quotes:', err);
      setQuotesError(err?.message || 'Failed to generate iconic quotes');
    } finally {
      setIsGeneratingQuotes(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      setUploadError(null);
      const imageUrl = await characterService?.uploadCharacterImage(playId, characterName, file);
      setCharacter(prev => ({ ...prev, imageUrl }));
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError(err?.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleLineClick = (line) => {
    navigate('/play-reading-interface', {
      state: {
        playId,
        lineId: line?.line_id
      }
    });
  };

  if (!playId || !characterName) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background'}`}>
        <RoleBasedNavigation
          user={userProfile}
          isAuthenticated={isAuthenticated}
          notifications={[]}
        />
        <main className="main-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Invalid Character</h2>
              <p className="text-muted-foreground mb-4">Character information is missing</p>
              <Button onClick={() => navigate('/characters-library')}>Back to Characters</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background'}`}>
        <RoleBasedNavigation
          user={userProfile}
          isAuthenticated={isAuthenticated}
          notifications={[]}
        />
        <main className="main-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading character details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && !character) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background'}`}>
        <RoleBasedNavigation
          user={userProfile}
          isAuthenticated={isAuthenticated}
          notifications={[]}
        />
        <main className="main-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Character</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchCharacterDetails}>Try Again</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background text-foreground'}`}>
      {/* Fixed reading progress bar */}
      <ReadingProgressBar progress={readingProgress} isDark={isDark} />

      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          {/* Back Button + Theme Toggle Row */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/characters-library')}
              iconName="ArrowLeft"
            >
              Back to Characters Library
            </Button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 border ${
                isDark
                  ? 'bg-gray-800 border-gray-600 text-amber-300 hover:bg-gray-700' :'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
              }`}
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={13} />
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
          </div>

          {/* Hero Section */}
          <div className={`rounded-lg border p-8 mb-8 transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card border-border'}`}>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Character Image */}
              <div className="flex-shrink-0">
                <div className={`relative w-64 h-64 rounded-lg overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                  {character?.imageUrl ? (
                    <img
                      src={character?.imageUrl}
                      alt={`${character?.name} from ${character?.playTitle}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="User" size={96} className="text-muted-foreground" />
                    </div>
                  )}

                  {/* Admin Image Upload */}
                  {isAdmin && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                      <label className="cursor-pointer flex items-center justify-center gap-2 text-white text-sm hover:text-primary transition-colors">
                        <Icon name="Upload" size={16} />
                        {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageUpload}
                          disabled={isUploadingImage}
                          className="hidden"
                        />
                      </label>
                      {uploadError && (
                        <p className="text-xs text-destructive mt-1">{uploadError}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Character Info */}
              <div className="flex-1">
                <h1 className={`text-4xl font-bold mb-2 ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.name}</h1>
                <p className="text-xl text-muted-foreground mb-4">
                  from <span className="font-semibold">{character?.playTitle}</span> by {character?.playAuthor}
                </p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="MessageSquare" size={18} className="text-primary" />
                      <span className="text-sm text-muted-foreground">Total Lines</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.lineCount || 0}</p>
                  </div>

                  <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="FileText" size={18} className="text-success" />
                      <span className="text-sm text-muted-foreground">Scenes</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.sceneAppearances || 0}</p>
                  </div>

                  <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="TrendingUp" size={18} className="text-warning" />
                      <span className="text-sm text-muted-foreground">Popularity</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.popularityScore || 0}</p>
                  </div>

                  <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Mic2" size={18} className="text-primary" />
                      <span className="text-sm text-muted-foreground">Voice Actor</span>
                    </div>
                    <p className={`text-sm font-semibold truncate ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>
                      {character?.voiceActor?.name || 'Not Assigned'}
                    </p>
                  </div>
                </div>

                {/* Voice Actor Section */}
                {character?.voiceActor && (
                  <div className={`rounded-lg p-4 mb-4 ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                    <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>
                      <Icon name="Mic2" size={16} />
                      Performed By
                    </h3>
                    <div className="flex items-center gap-3">
                      {character?.voiceActor?.headshotUrl ? (
                        <img
                          src={character?.voiceActor?.headshotUrl}
                          alt={character?.voiceActor?.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={24} className="text-primary" />
                        </div>
                      )}
                      <div>
                        <p className={`font-semibold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.voiceActor?.name}</p>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => navigate(`/performer-profile/${character?.voiceActor?.id}`)}
                          className="p-0 h-auto text-xs"
                        >
                          View Profile
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Character Analysis */}
          <div className={`rounded-lg border p-6 mb-8 transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card border-border'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>
                <Icon name="Sparkles" size={24} className="text-primary" />
                AI Character Analysis
              </h2>
              {isAdmin && !aiAnalysis && (
                <Button
                  onClick={handleGenerateAnalysis}
                  disabled={isGeneratingAnalysis}
                  iconName={isGeneratingAnalysis ? 'Loader2' : 'Sparkles'}
                  className={isGeneratingAnalysis ? 'animate-spin' : ''}
                >
                  {isGeneratingAnalysis ? 'Generating...' : 'Generate Analysis'}
                </Button>
              )}
              {isAdmin && aiAnalysis && (
                <Button
                  onClick={handleGenerateAnalysis}
                  disabled={isGeneratingAnalysis}
                  variant="outline"
                  size="sm"
                  iconName={isGeneratingAnalysis ? 'Loader2' : 'RefreshCw'}
                  className={isGeneratingAnalysis ? 'animate-spin' : ''}
                >
                  {isGeneratingAnalysis ? 'Regenerating...' : 'Regenerate'}
                </Button>
              )}
            </div>

            {aiAnalysis ? (
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground whitespace-pre-wrap">{aiAnalysis}</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Sparkles" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {isAdmin ? 'Click "Generate Analysis" to create AI-powered insights about this character' : 'AI analysis not yet available for this character'}
                </p>
              </div>
            )}
          </div>

          {/* Iconic Quotes Section */}
          <div className="mb-8">
            <IconicQuotesSection
              quotes={iconicQuotes}
              isGenerating={isGeneratingQuotes}
              onGenerate={isAdmin ? handleGenerateQuotes : null}
              onLineClick={handleLineClick}
              characterName={characterName}
              isAdmin={isAdmin}
            />
            {quotesError && (
              <p className="text-sm text-destructive mt-2">{quotesError}</p>
            )}
          </div>

          {/* Character Relationship Network */}
          <div className={`rounded-lg border p-6 mb-8 transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card border-border'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>
                <Icon name="Network" size={24} className="text-primary" />
                Character Relationships
              </h2>
              {networkError && (
                <Button
                  onClick={fetchCharacterNetwork}
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                >
                  Retry
                </Button>
              )}
            </div>

            {isLoadingNetwork ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                  <p className="text-muted-foreground">Loading character relationships...</p>
                </div>
              </div>
            ) : networkError ? (
              <div className={`flex items-center justify-center h-96 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                <div className="text-center">
                  <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{networkError}</p>
                  <Button onClick={fetchCharacterNetwork} size="sm">Try Again</Button>
                </div>
              </div>
            ) : networkData && networkData?.nodes?.length > 0 ? (
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  This interactive network diagram shows how <span className={`font-semibold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{characterName}</span> connects 
                  with other characters through shared scenes in <span className={`font-semibold ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>{character?.playTitle}</span>. 
                  Thicker lines indicate more scenes together.
                </p>
                <CharacterNetworkDiagram
                  nodes={networkData?.nodes}
                  links={networkData?.links}
                  onNodeClick={(node) => {
                    if (node?.id !== characterName) {
                      navigate('/character-detail-page', {
                        state: {
                          playId,
                          characterName: node?.id
                        }
                      });
                    }
                  }}
                />
              </div>
            ) : (
              <div className={`flex items-center justify-center h-96 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-muted'}`}>
                <div className="text-center">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    This character appears alone or has no scene interactions with other characters
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Line Shortcuts */}
          <div className={`rounded-lg border p-6 transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card border-border'}`}>
            <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-card-foreground'}`}>
              <Icon name="List" size={24} />
              Character Lines
            </h2>

            {character?.lines && character?.lines?.length > 0 ? (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {character?.lines?.slice(0, 50)?.map((line, index) => (
                  <div
                    key={line?.id || index}
                    onClick={() => handleLineClick(line)}
                    className={`rounded-lg p-4 cursor-pointer transition-colors group ${
                      isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                            {line?.line_id || `Line ${line?.line_number}`}
                          </span>
                          {line?.act?.act_number && line?.scene?.scene_number && (
                            <span className="text-xs text-muted-foreground">
                              Act {line?.act?.act_number}, Scene {line?.scene?.scene_number}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-200' : 'text-card-foreground'}`}>{line?.dialogue}</p>
                      </div>
                      <Icon
                        name="ArrowRight"
                        size={20}
                        className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0"
                      />
                    </div>
                  </div>
                ))}
                {character?.lines?.length > 50 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Showing first 50 of {character?.lines?.length} lines
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No lines available for this character</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CharacterDetailPage;